import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

interface EmptyStateProps {
  title: string;
  description?: string;
  /** Defaults to an inbox glyph. */
  icon?: React.ReactNode;
  /** Optional call to action, e.g. "Book an appointment". */
  action?: React.ReactNode;
  /** Tighter padding for use inside a table cell. */
  dense?: boolean;
}

/**
 * Replaces bare "No records found" text. An empty screen should say what
 * belongs there and offer the next step.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  icon,
  action,
  dense = false,
}) => (
  <Stack
    alignItems="center"
    justifyContent="center"
    spacing={1.5}
    sx={{ py: dense ? 5 : 8, px: 3, textAlign: 'center' }}
  >
    <Box
      sx={{
        width: 52,
        height: 52,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        color: 'text.secondary',
        backgroundColor: (t) => alpha(t.palette.text.primary, t.palette.mode === 'light' ? 0.04 : 0.08),
        '& svg': { fontSize: 26 },
      }}
    >
      {icon ?? <InboxOutlinedIcon />}
    </Box>

    <Typography variant="subtitle1" fontWeight={600}>
      {title}
    </Typography>

    {description && (
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '42ch' }}>
        {description}
      </Typography>
    )}

    {action && <Box sx={{ pt: 1 }}>{action}</Box>}
  </Stack>
);

export default EmptyState;
